import axios from 'axios'

const BANNER_TYPE = 'баннер'

function readEnv(name) {
  const raw = process.env[name]
  return raw == null ? '' : String(raw).trim().replace(/^['"]|['"]$/g, '')
}

function expandEnv(value) {
  if (!value) return ''
  let out = value
  for (let i = 0; i < 5; i += 1) {
    const next = out.replace(/\$\{([A-Z0-9_]+)\}/g, (_, name) => readEnv(name))
    if (next === out) break
    out = next
  }
  return out.replace(/\/+$/, '')
}

export const BRAND_ID = readEnv('VITE_APP_BRAND_ID') || '17'
export const BRAND_SLUG = readEnv('VITE_APP_BRAND_SLUG') || 'tcl'
export const BACKEND_URL = expandEnv(readEnv('VITE_APP_BACKEND_URL')) || 'http://admin-brands.loc'
export const API_URL = expandEnv(readEnv('VITE_APP_BACKEND_URL_API')) || `${BACKEND_URL}/api`

export function mediaUrl(path) {
  if (!path) return ''
  const value = String(path).trim()
  if (!value) return ''
  if (value.startsWith('http') || value.startsWith('//')) return value
  if (value.startsWith('/')) return `${BACKEND_URL}${value}`
  return `${BACKEND_URL}/${BRAND_SLUG}/${value.replace(/^\/+/, '')}`
}

export function certificateFileUrl(path) {
  const raw = String(path || '').trim()
  if (!raw) return ''
  const file = raw.split(/[\\/]/).pop()
  if (!file) return ''
  const params = new URLSearchParams(brandParams({ file }))
  return `${API_URL}/certificates/public/file?${params.toString()}`
}

function brandParams(extra = {}) {
  const params = { brand_id: BRAND_ID, ...extra }
  Object.keys(params).forEach((key) => {
    if (params[key] === undefined || params[key] === null || params[key] === '') {
      delete params[key]
    }
  })
  return params
}

async function get(path, params = {}) {
  const { data } = await axios.get(`${API_URL}${path}`, {
    params: brandParams(params),
    headers: { Accept: 'application/json' },
  })
  return data
}

export function parseNewsTags(raw) {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (item && typeof item === 'object') {
          const title = String(item.title || item.id || '').trim()
          return title ? { id: item.id || title, title } : null
        }
        const title = String(item || '').trim()
        return title ? { id: title, title } : null
      })
      .filter(Boolean)
  }

  const text = String(raw || '').trim()
  if (!text) return []

  if (text.startsWith('{')) {
    try {
      const decoded = JSON.parse(text)
      if (decoded && typeof decoded === 'object') {
        return Object.keys(decoded)
          .map((key) => String(key || decoded[key] || '').trim())
          .filter(Boolean)
          .map((title) => ({ id: title, title }))
      }
    } catch (e) {
      // comma-separated fallback
    }
  }

  return text
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((title) => ({ id: title, title }))
}

export function mapNewsCard(item) {
  if (!item) return null
  const cover = item.cover_url || item.cover || ''
  return {
    ...item,
    filename: cover,
    date: item.date_public || item.date || null,
    text: item.description || item.text || '',
    tags: parseNewsTags(item.tags),
  }
}

export async function fetchNewsFilters({ type } = {}) {
  return get('/news/public/filters', {
    type,
    exclude_type: BANNER_TYPE,
  })
}

export async function fetchNewsList({ type, tags = [], page = 1, limit = 10, excludeBanner = true } = {}) {
  const params = { page, limit }
  if (type) params.type = type
  if (excludeBanner) params.exclude_type = BANNER_TYPE
  if (tags.length) params.tags = tags.map((tag) => tag.id || tag).join(',')

  const payload = await get('/news/public', params)
  const data = Array.isArray(payload?.data) ? payload.data.map(mapNewsCard) : []
  return {
    data,
    count: payload?.meta?.total ?? data.length,
    meta: payload?.meta || null,
  }
}

export async function fetchRelatedNews({ categoryTitle, subtypeTitle, limit = 6 } = {}) {
  const tags = [categoryTitle, subtypeTitle]
    .map((value) => String(value || '').trim())
    .filter(Boolean)

  if (!tags.length) {
    return []
  }

  const payload = await fetchNewsList({ tags, limit, excludeBanner: true })
  return payload.data || []
}

export async function fetchNewsDetail(id) {
  const payload = await get(`/news/public/${id}`)
  const card = mapNewsCard(payload)
  if (!card) return null
  return {
    ...card,
    content: (payload.body_html || payload.content || card.text || ''),
    text: (payload.body_html || payload.content || card.text || ''),
  }
}

export async function fetchBanners() {
  const payload = await get('/news/public', {
    type: BANNER_TYPE,
    limit: 50,
    page: 1,
  })
  const items = Array.isArray(payload?.data) ? payload.data : []
  return items
    .slice()
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    .map((item) => ({
      ...item,
      image: item.cover_url || item.cover || '',
      link: item.link || '#',
      isDynamic: true,
    }))
}

export async function fetchCertificate(inn) {
  return get('/certificates/public', { inn })
}

export async function fetchCertificateGroups() {
  const payload = await get('/certificates/public/list')
  const groups = Array.isArray(payload?.data) ? payload.data : []
  return Promise.all(groups.map(async (group, index) => {
    const rows = Array.isArray(group.data) ? group.data : []
    const items = await Promise.all(rows.map(async (item) => {
      const filename = item.filename || item.file_cert || ''
      if (!filename) return null
      const source = await fetchCertificateBlobUrl(filename).catch(() => '')
      return {
        ...item,
        filename,
        source: source || certificateFileUrl(filename),
      }
    }))
    const data = items.filter(Boolean)
    return {
      id: `cert-${index}`,
      title: String(group.title || '').trim(),
      count: data.length,
      data,
    }
  }))
}

export async function fetchSeo(path) {
  return get('/seo/public', { path: path || '/' })
}

export async function fetchProductCategories() {
  const payload = await get('/products/public/categories', { tree: 1 })
  return Array.isArray(payload?.data) ? payload.data : []
}

export async function fetchProducts({ page = 1, limit = 12, categoryId, subtypeId, search } = {}) {
  const payload = await get('/products/public', {
    page,
    limit,
    category_id: categoryId,
    subtype_id: subtypeId,
    search,
  })
  return {
    data: Array.isArray(payload?.data) ? payload.data : [],
    count: payload?.meta?.total ?? 0,
  }
}

export async function fetchProduct(id) {
  const payload = await get(`/products/public/${id}`)
  return payload?.data || payload || null
}

export async function fetchProductMatches(id) {
  if (!id) return []
  const payload = await get(`/products/public/${id}/matches`)
  return Array.isArray(payload?.data) ? payload.data : []
}

const BLANK_OPTION = /^(?:\(?\s*пусто\s*\)?|н\/д|n\/a|null|—|-)$/i

export function cleanOptionPart(value) {
  const text = String(value || '').trim()
  if (!text || BLANK_OPTION.test(text)) return null
  return text
}

export function cleanOptionTitle(value) {
  return String(value || '')
    .split(',')
    .map((part) => cleanOptionPart(part))
    .filter(Boolean)
    .join(', ')
}

export function cleanSelectionOption(item) {
  if (item == null || item === '') return null
  if (typeof item !== 'object') {
    const title = cleanOptionTitle(item)
    return title ? { id: title, title } : null
  }

  const body = cleanOptionPart(item.body)
  const engine = cleanOptionPart(item.engine)
  const volume = cleanOptionPart(item.volume)
  const fromParts = [body, engine, volume].filter(Boolean).join(', ')
  const title = fromParts || cleanOptionTitle(item.title || item.name || item.value || '')
  if (!title) return null

  return {
    ...item,
    id: item.id || title,
    title,
    body,
    engine,
    volume,
  }
}

export function cleanSelectionOptions(list) {
  const seen = new Set()
  const result = []
  ;(Array.isArray(list) ? list : []).forEach((item) => {
    const cleaned = cleanSelectionOption(item)
    if (!cleaned) return
    const key = String(cleaned.title).trim().toLowerCase()
    if (!key || seen.has(key)) return
    seen.add(key)
    result.push(cleaned)
  })
  return result
}

function asOptionList(payload) {
  const rows = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : []
  return cleanSelectionOptions(rows)
}

const selectionOptionCache = new Map()
const selectionOptionInflight = new Map()

export async function fetchSelectionOptions(field, filters = {}) {
  const key = JSON.stringify({
    field,
    make: filters.make || '',
    model: filters.model || '',
    body: filters.body || '',
  })
  if (selectionOptionCache.has(key)) {
    return selectionOptionCache.get(key).slice()
  }

  let pending = selectionOptionInflight.get(key)
  if (!pending) {
    pending = get('/selections/public/options', {
      field,
      make: filters.make,
      model: filters.model,
      body: filters.body,
    }).then((payload) => {
      const list = asOptionList(payload)
      selectionOptionCache.set(key, list)
      selectionOptionInflight.delete(key)
      return list
    }).catch((error) => {
      selectionOptionInflight.delete(key)
      throw error
    })
    selectionOptionInflight.set(key, pending)
  }

  const list = await pending
  return list.slice()
}

export async function findMarkTitle(title) {
  const marks = await fetchSelectionOptions('make')
  const needle = String(title || '').trim().toLowerCase()
  const found = marks.find((item) => String(item.title).trim().toLowerCase() === needle)
  return found ? found.title : String(title || '').trim()
}

export function displayProductTitle(title) {
  const text = String(title || '').trim()
  if (!text.startsWith('{')) return text
  try {
    const decoded = JSON.parse(text)
    if (decoded && typeof decoded === 'object') {
      const values = Object.values(decoded).map((value) => String(value || '').trim()).filter(Boolean)
      if (values.length) return values[0]
    }
  } catch (e) {
    return text
  }
  return text
}

export function productVolumes(articles) {
  const seen = new Set()
  const result = []
  ;(articles || []).forEach((article) => {
    const raw = article && (article.size || article.volume)
    const volume = parseVolume(raw)
    if (!volume || seen.has(volume)) return
    seen.add(volume)
    result.push({ ...article, volume })
  })
  return result
}

export function resolveProductFile(file, productId) {
  if (!file) return ''
  const value = String(file).trim()
  if (!value) return ''
  if (value.startsWith('http') || value.startsWith('//') || value.startsWith('/')) {
    return mediaUrl(value)
  }
  if (productId) {
    return mediaUrl(`/${BRAND_SLUG}/files/products/${productId}/${value}`)
  }
  return mediaUrl(value)
}

function rudderLabel(rudder) {
  const value = Number(rudder)
  if (value === 2) return 'RHD'
  if (value === 1) return 'LHD'
  return null
}

function formatEngineVolume(volume) {
  const raw = String(volume || '').trim()
  if (!raw) return ''
  if (/v$/i.test(raw)) return raw
  return `${raw}V`
}

const SELECTION_TYPE_ORDER = [
  'engine',
  'transmission',
  'transfer',
  'drive_front',
  'drive_back',
  'antifreeze',
  'fluid',
]

function selectionTypeRank(type) {
  const index = SELECTION_TYPE_ORDER.indexOf(String(type || ''))
  return index === -1 ? SELECTION_TYPE_ORDER.length : index
}

function inferSelectionType(item) {
  if (item.selection_type) return item.selection_type
  const category = String(item.category_title || '').toLowerCase()
  const title = String(item.product_title || item.title || '').toLowerCase()
  const drive = String((item.car && item.car.drive) || '').toLowerCase()
  if (category.includes('моторн')) return 'engine'
  if (category.includes('антифриз')) return 'antifreeze'
  if (/cvtf|atf|акпп|вариатор/.test(title)) return 'transmission'
  if (title.includes('раздат')) return 'transfer'
  if (/gear|gl-5|gl5|дифференциал/.test(title)) {
    if (drive.includes('перед')) return 'drive_front'
    return 'drive_back'
  }
  return ''
}

export async function fetchSelectionProducts({
  make,
  model,
  body,
  engine,
  volume,
  car_info_id: carInfoId,
} = {}) {
  const { data } = await axios.post(`${API_URL}/selections/public/search`, {
    brand_id: Number(BRAND_ID),
    brand_slug: BRAND_SLUG,
    method: 'brand',
    make: make || '',
    model: model || '',
    body: body || '',
    engine: engine || '',
    volume: volume || '',
    car_info_id: carInfoId || undefined,
  }, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
  const rows = Array.isArray(data?.data) ? data.data : []
  const grouped = new Map()

  rows.forEach((item) => {
    const car = item.car || {}
    const category = item.category_title || item.subtype_title || 'Продукция TCL'
    const type = inferSelectionType(item)
    const fillVolume = item.selection_volume || ''
    const transmission = car.transmission || ''
    const drive = car.drive || ''
    const carBody = car.body || body || ''
    const engineTitle = car.engine || engine || ''
    const engineVolume = formatEngineVolume(car.volume_label || car.volume || volume || '')
    const wheel = rudderLabel(car.rudder)
    const productId = item.product_id || item.id
    const groupKey = [
      item.category_id || category,
      type || `product:${productId}`,
      fillVolume,
      transmission,
      drive,
      carBody,
      engineTitle,
      engineVolume,
      wheel || '',
    ].join('|')

    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, {
        category,
        category_sort: Number(item.category_sort) || 0,
        type,
        transmission,
        drive,
        wheel,
        body: carBody,
        engineTitle,
        engineVolume,
        volume: fillVolume,
        items: [],
      })
    }

    const bucket = grouped.get(groupKey)
    let product = bucket.items.find((entry) => entry.id === productId)
    if (!product) {
      product = {
        id: productId,
        title: displayProductTitle(item.product_title || item.title),
        file: item.product_file || item.file,
        volumes: [],
      }
      bucket.items.push(product)
    }
    const productVolume = parseVolume(item.size)
    if (productVolume && !product.volumes.includes(productVolume)) {
      product.volumes.push(productVolume)
    }
  })

  return Array.from(grouped.values()).sort((a, b) => {
    if (a.category_sort !== b.category_sort) return a.category_sort - b.category_sort
    if (a.category !== b.category) return String(a.category).localeCompare(String(b.category), 'ru')
    const typeDiff = selectionTypeRank(a.type) - selectionTypeRank(b.type)
    if (typeDiff !== 0) return typeDiff
    return String(a.volume || '').localeCompare(String(b.volume || ''), 'ru')
  })
}

export async function fetchCertificateBlobUrl(path) {
  const url = certificateFileUrl(path)
  if (!url) return ''
  const { data } = await axios.get(url, {
    responseType: 'blob',
    headers: { Accept: 'application/pdf' },
  })
  if (!data || !data.size) return ''
  return URL.createObjectURL(data)
}

export async function fetchProductCertificates(productId) {
  if (!productId) return []
  const payload = await get('/certificates/public/by-product', { product_id: productId })
  const rows = Array.isArray(payload?.data) ? payload.data : []
  const certs = await Promise.all(rows.map(async (item) => {
    const filename = item.filename || item.file_cert || ''
    if (!filename) return null
    const source = await fetchCertificateBlobUrl(filename).catch(() => '')
    return {
      ...item,
      filename,
      source: source || certificateFileUrl(filename),
    }
  }))
  return certs.filter(Boolean)
}

export function parseArticleGallery(description) {
  const text = String(description || '').trim()
  if (!text.startsWith('[') || !text.endsWith(']')) return []
  return text
    .slice(1, -1)
    .split(',')
    .map((part) => part.trim().replace(/^['"]|['"]$/g, ''))
    .filter(Boolean)
}

/** Только картинки артикула: file первой, затем список из description. */
export function buildArticleFiles(article) {
  const main = String(article?.file || '').trim()
  const gallery = parseArticleGallery(article?.description)
  const seen = new Set()
  const files = []
  ;[main, ...gallery].forEach((file) => {
    const value = String(file || '').trim()
    if (!value || seen.has(value)) return
    seen.add(value)
    files.push(value)
  })
  return files
}

export async function fetchWhereToBuy(path) {
  const { data } = await axios.get(`${API_URL}/get/${path}`, {
    headers: { Accept: 'application/json' },
  })
  return data
}

export async function postFeedback(form, token) {
  const { data } = await axios.post(`${API_URL}/feedbacks`, {
    brand_id: Number(BRAND_ID),
    brand_slug: BRAND_SLUG,
    name: form.name || '',
    phone: form.phone || '',
    email: form.email || '',
    city: form.city || '',
    message: form.message || '',
    subject: form.type === 2 ? 'Партнерам' : 'Обратная связь',
    token: token || form.token || '',
  }, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
  return data
}

export function parseVolume(value) {
  const match = String(value || '').replace(',', '.').match(/(\d+(?:\.\d+)?)/)
  return match ? Number(match[1]) : 0
}
